import type { Car } from "@/types/cars";

export type PublicVehicleStatus = NonNullable<Car["status"]>;

const STATUS_ALIASES: Record<string, PublicVehicleStatus> = {
    active: "active",
    available: "active",
    hidden: "hidden",
    deposit: "deposit",
    "deposit taken": "deposit",
    sold: "sold",
    cancelled: "cancelled",
    "coming soon": "coming soon",
    "coming-soon": "coming soon",
    "sales pending": "sales pending",
    "sale pending": "sales pending",
};

const COMING_SOON_PRICE_THRESHOLD = 100;
const STALE_STATUS_WINDOW_MS = 24 * 60 * 60 * 1000;

interface VehicleStatusInput {
    status?: string | null | undefined;
    updatedAt?: number | string | null | undefined;
    price?: number | string | null | undefined;
    now?: number | undefined;
}

interface VehicleStatusRecord {
    status?: string | null | undefined;
    updatedAt?: number | string | null | undefined;
    price?: number | string | null | undefined;
}

interface VehicleHit<T extends VehicleStatusRecord> {
    document: T;
}

function toFiniteNumber(value: number | string | null | undefined): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return undefined;
}

export function normalizeVehicleStatus(status?: string | null): PublicVehicleStatus | undefined {
    if (!status) {
        return undefined;
    }

    const normalizedStatus = status.trim().toLowerCase().replace(/\s+/g, " ");
    return STATUS_ALIASES[normalizedStatus];
}

export function resolveVehicleStatus({
    status,
    updatedAt,
    price,
    now = Date.now()
}: VehicleStatusInput): PublicVehicleStatus | undefined {
    let resolvedStatus = normalizeVehicleStatus(status);
    const updatedAtTimestamp = toFiniteNumber(updatedAt);
    const normalizedPrice = toFiniteNumber(price);
    const isStale =
        typeof updatedAtTimestamp === "number" && updatedAtTimestamp < now - STALE_STATUS_WINDOW_MS;

    if (!process.env["NEXT_PUBLIC_INVENTORY_API_URL"] && (!resolvedStatus || resolvedStatus === "active") && isStale) {
        resolvedStatus = "coming soon";
    }

    if (
        typeof normalizedPrice === "number" &&
        normalizedPrice < COMING_SOON_PRICE_THRESHOLD &&
        resolvedStatus === "active"
    ) {
        resolvedStatus = "coming soon";
    }

    return resolvedStatus;
}

export function normalizeVehicleDocumentStatus<T extends VehicleStatusRecord>(vehicle: T, now?: number): T {
    const resolvedStatus = resolveVehicleStatus({
        status: vehicle.status,
        updatedAt: vehicle.updatedAt,
        price: vehicle.price,
        now
    });

    if (!resolvedStatus || vehicle.status === resolvedStatus) {
        return vehicle;
    }

    return {
        ...vehicle,
        status: resolvedStatus
    };
}

export function normalizeVehicleHits<T extends VehicleStatusRecord>(
    hits: Array<VehicleHit<T>>,
    now?: number
): Array<VehicleHit<T>> {
    return hits.map((hit) => {
        const normalizedDocument = normalizeVehicleDocumentStatus(hit.document, now);

        if (normalizedDocument === hit.document) {
            return hit;
        }

        return {
            ...hit,
            document: normalizedDocument
        };
    });
}

/** Only active inventory is offered as in stock; holds are not purchasable stock. */
export function schemaVehicleAvailability(status?: string | null): string {
    const normalized = normalizeVehicleStatus(status);
    if (normalized === 'active') return 'https://schema.org/InStock';
    if (normalized === 'sold') return 'https://schema.org/SoldOut';
    return 'https://schema.org/OutOfStock';
}
